package com.zi.support.shared

/** [id] is the link's Firestore document id. */
data class Portal(val name: String, val url: String, val id: String? = null)

data class CustomerPortals(val customer: String, val portals: List<Portal>)

/**
 * Portals: every company's web portal links. They all live in Firestore [COLLECTION] as
 * { company, name, url, createdBy, createdAt, updatedBy?, updatedAt? }, so every one can be edited and deleted in the
 * apps; [grouped] turns them into the per-company list. (scripts/seed-portal-links.js loaded the original links.)
 */
object PortalDirectory {
    const val COLLECTION = "portalLinks"

    /** A pasted link made usable: trimmed, https:// added when no scheme. Null when it can't be a web address. */
    fun normalizeUrl(input: String): String? {
        val url = input.trim().let { if (it.contains("://")) it else "https://$it" }
        val host = url.substringAfter("://").substringBefore('/')
        return url.takeIf {
            (it.startsWith("https://") || it.startsWith("http://")) && it.none(Char::isWhitespace) && '.' in host && it.length <= 500
        }
    }

    /** A fallback name for a link saved without one: its last path segment ("depoflow" -> "Depoflow"), else its host. */
    fun nameFor(url: String): String {
        val rest = url.substringAfter("://").substringBefore('?').substringBefore('#')
        val segment = rest.substringAfter('/', "").trimEnd('/').substringAfterLast('/')
        return if (segment.isBlank()) rest.substringBefore('/')
        else segment.replace('-', ' ').replace('_', ' ').replaceFirstChar { it.uppercase() }
    }

    /** Every company a link can be added under: the ticket companies plus Settings companies. */
    val companies: List<String> get() = CompanyDirectory.names(listOf(
        "ACB", "Avemel", "Bees Cartridge", "Delta", "FCL", "Frenz Logistics", "Gantrans", "Grandeur", "KPH", "Renwood",
        "Rungatrans", "STS", "Texas", "Transport", "Tyetrans", "Xmoor", "ZI",
    ))

    /** [links] ([company] to [Portal]) grouped by company (A-Z, case-insensitive), links A-Z within each. */
    fun grouped(links: List<Pair<String, Portal>>): List<CustomerPortals> =
        links.groupBy { it.first.trim().lowercase() }.values
            .map { group -> CustomerPortals(group.first().first.trim(), group.map { it.second }.sortedBy { it.name.lowercase() }) }
            .sortedBy { it.customer.lowercase() }
}
